/**
 * WebSocket client gửi transcript (user / bot) lên PieSocket và đồng thời POST lên server PHP (MySQL).
 * Tất cả message socket dùng chung một format:
 * { "time": "ISO8601", "whom": "user"|"bot", "text": "...", "conversationId": "uuid", "sentiment": number|null }
 */

export const PIESOCKET_URL =
  "wss://free.blr2.piesocket.com/v3/1?api_key=esqC7ggDb2Hhf9PGhvHHqCnWRpehUK86uqQ3xsw6&notify_self=1";

const INSERT_TRANSCRIPT_URL = "https://infodation-ai.com/VNAVoicebot2/insert_transcript.php";

export type TranscriptWhom = "user" | "bot";

/** Format thống nhất mọi message socket (và PHP): time, whom, text, conversationId, sentiment. */
export interface TranscriptPayload {
  time: string;
  whom: TranscriptWhom;
  text: string;
  conversationId: string;
  sentiment: number | null;
}

let socket: WebSocket | null = null;
const queue: TranscriptPayload[] = [];

function flushQueue(): void {
  if (!socket || socket.readyState !== WebSocket.OPEN) return;
  while (queue.length > 0) {
    const payload = queue.shift();
    if (payload) {
      try {
        socket.send(JSON.stringify(payload));
      } catch {
        queue.unshift(payload);
        break;
      }
    }
  }
}

function ensureSocket(): void {
  if (socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING) return;
  try {
    socket = new WebSocket(PIESOCKET_URL);
    socket.onopen = () => flushQueue();
    socket.onclose = () => { socket = null; };
    socket.onerror = () => { socket = null; };
  } catch {
    socket = null;
  }
}

/** Gửi cùng payload (đúng format) lên server PHP để lưu MySQL (fire-and-forget). */
function sendToServer(payload: TranscriptPayload): void {
  const body: TranscriptPayload = {
    time: payload.time,
    whom: payload.whom,
    text: payload.text,
    conversationId: payload.conversationId,
    sentiment: payload.sentiment,
  };
  fetch(INSERT_TRANSCRIPT_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).catch((err) => console.warn("[transcript] PHP insert failed:", err));
}

/**
 * Gửi một dòng transcript lên PieSocket và đồng thời lên server PHP (MySQL).
 * @param conversationId - UUID của cuộc hội thoại (mỗi lần connect tạo mới) để phân biệt nhiều cuộc gọi đồng thời.
 * @param sentiment - Điểm 1-100 (chỉ với whom=bot, parse từ [SENTIMENT sofar : X] trong systemInstruction).
 */
export function sendTranscript(
  whom: TranscriptWhom,
  text: string,
  conversationId: string,
  sentiment?: number
): void {
  const t = (typeof text === "string" ? text : "").trim();
  if (!t) return;

  const sentimentValue =
    sentiment != null && sentiment >= 1 && sentiment <= 100 ? Math.round(sentiment) : null;
  const payload: TranscriptPayload = {
    time: new Date().toISOString(),
    whom,
    text: t,
    conversationId,
    sentiment: sentimentValue,
  };

  sendToServer(payload);

  ensureSocket();
  if (socket?.readyState === WebSocket.OPEN) {
    try {
      socket.send(JSON.stringify(payload));
    } catch {
      queue.push(payload);
    }
  } else {
    queue.push(payload);
  }
}

/**
 * Đóng kết nối (gọi khi disconnect Live API).
 */
export function closeTranscriptSocket(): void {
  if (socket) {
    try { socket.close(); } catch { /* ignore */ }
    socket = null;
  }
  queue.length = 0;
}
